import React, { useState, useMemo } from 'react';
import { Inbox, Upload, Filter } from 'lucide-react';
import { useAppSelector } from '../../redux/hooks';
import { useListAdminSitesQuery, useListPortfolioSitesQuery, useGetSiteArtefactsQuery, type Artefact } from '../../redux/api/portfolioApi';
import { useListSiteEventsQuery } from '../../redux/api/eventsApi';
import type { SiteEvent } from '../../redux/api/eventsApi';
import { usePortfolioEvents } from '../../hooks/usePortfolioEvents';
import InboxItem from '../inbox/InboxItem';
import type { InboxItemProps } from '../inbox/InboxItem';
import InboxSummary from '../inbox/InboxSummary';
import KnowledgeDropZone from '../dashboard/KnowledgeDropZone';

type ScopeMode = 'portfolio' | 'site';
type FilterMode = 'all' | 'documents' | 'emails' | 'agent';

// ── Helpers ──────────────────────────────────────────────────────────

function getDateGroup(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfWeek = new Date(startOfToday);
  startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());

  if (date >= startOfToday) return 'Today';
  if (date >= startOfWeek) return 'This Week';
  return 'Earlier';
}

const GROUP_ORDER: Record<string, number> = { 'Today': 0, 'This Week': 1, 'Earlier': 2 };

// ── Mock data generators ─────────────────────────────────────────────

const MOCK_EMAILS: InboxItemProps[] = [
  {
    type: 'email',
    title: 'RE: Inverter fault report - Site inspection follow-up',
    subtitle: 'from: maintenance@solarops.com',
    timestamp: new Date(Date.now() - 3 * 3600000).toISOString(),
    outputs: [
      { type: 'event', id: 'mock-ev-1', title: 'Inverter #12 fault detected', severity: 'high' },
      { type: 'task', id: 'mock-t-1', title: 'Schedule inverter inspection' },
    ],
  },
  {
    type: 'email',
    title: 'Monthly performance summary - February 2026',
    subtitle: 'from: analytics@engramiq.com',
    timestamp: new Date(Date.now() - 48 * 3600000).toISOString(),
    outputs: [
      { type: 'event', id: 'mock-ev-2', title: 'Below-target generation detected', severity: 'medium' },
      { type: 'update', id: 'mock-u-1', title: 'Performance dashboard updated' },
    ],
  },
];

const MOCK_AGENT: InboxItemProps[] = [
  {
    type: 'agent',
    title: 'AI Analysis: Anomaly detected in tracker data',
    subtitle: 'Automated intelligence pipeline',
    timestamp: new Date(Date.now() - 6 * 3600000).toISOString(),
    outputs: [
      { type: 'event', id: 'mock-ev-3', title: 'Tracker row 5 misalignment', severity: 'critical' },
      { type: 'task', id: 'mock-t-2', title: 'Dispatch tracker maintenance crew' },
      { type: 'task', id: 'mock-t-3', title: 'Review historical tracker alignment data' },
    ],
  },
];

// ── Component ────────────────────────────────────────────────────────

const ProjectInboxTab: React.FC = () => {
  const currentSite = useAppSelector(state => state.site.currentSite);
  const siteId = currentSite?.site_id || currentSite?.id;
  const siteName = currentSite?.site_name || currentSite?.name || 'Current Site';

  const [scope, setScope] = useState<ScopeMode>('site');
  const [filter, setFilter] = useState<FilterMode>('all');
  const [uploadOpen, setUploadOpen] = useState(false);

  // Org scoping for super admins
  const profile = useAppSelector((state) => state.auth.profile);
  const organizationState = useAppSelector((s) => s.organization);
  const isSuperAdmin = (profile.email || '').toLowerCase().endsWith('@engramiq.com');
  const currentOrgId = isSuperAdmin ? organizationState.currentOrganization?.id ?? null : null;

  const tenantId = currentSite?.tenant_id;
  const { data: artefactsData, isLoading: docsLoading } = useGetSiteArtefactsQuery(
    { tenant_id: tenantId!, site_id: siteId!, limit: 50 },
    { skip: scope === 'portfolio' || !siteId || !tenantId }
  );
  const documents: Artefact[] = artefactsData?.items ?? [];
  const selectedPortfolioId = useAppSelector((state) => state.portfolio.selectedPortfolioId);
  const usePortfolioScoping = !!selectedPortfolioId;
  const adminArgs = currentOrgId ? { organization_id: currentOrgId, limit: 100 } : { limit: 100 };
  const { data: adminSitesData } = useListAdminSitesQuery(adminArgs, {
    skip: !isSuperAdmin || usePortfolioScoping,
  });
  const { data: portfolioSitesData } = useListPortfolioSitesQuery(
    { portfolio_id: selectedPortfolioId || '' },
    { skip: !selectedPortfolioId }
  );
  const portfolioSites = useMemo(() => {
    const raw = usePortfolioScoping ? portfolioSitesData : adminSitesData;
    return (raw as any)?.items ?? [];
  }, [usePortfolioScoping, portfolioSitesData, adminSitesData]);

  // Site-scoped events
  const { data: eventsData } = useListSiteEventsQuery(
    { siteId: siteId || '', limit: 50, sort_by: 'occurred_at', sort_dir: 'desc' },
    { skip: scope === 'portfolio' || !siteId }
  );

  // Portfolio-scoped events (loads from all sites)
  const { events: portfolioEventsRaw, isLoading: portfolioEventsLoading, siteNameMap } = usePortfolioEvents(
    portfolioSites,
    { enabled: scope === 'portfolio' }
  );

  // Pick events based on scope
  const activeEvents: SiteEvent[] = useMemo(() => {
    if (scope === 'portfolio') return portfolioEventsRaw;
    return eventsData?.items || [];
  }, [scope, portfolioEventsRaw, eventsData]);

  // Transform events into inbox items
  const eventItems: InboxItemProps[] = useMemo(() => {
    if (!activeEvents.length) return [];
    return activeEvents.map((event: SiteEvent) => ({
      type: 'agent' as const,
      title: event.title,
      subtitle: scope === 'portfolio' && siteNameMap[event.site_id]
        ? `${siteNameMap[event.site_id]} · ${event.event_type} - ${event.severity}`
        : `${event.event_type} - ${event.severity} severity`,
      timestamp: event.occurred_at,
      outputs: [
        {
          type: 'event' as const,
          id: event.id,
          title: event.title,
          severity: event.severity,
        },
        ...(event.description ? [{
          type: 'update' as const,
          id: `${event.id}-desc`,
          title: event.description.slice(0, 100) + (event.description.length > 100 ? '...' : ''),
        }] : []),
      ],
      onOpenSource: () => {
        window.dispatchEvent(new CustomEvent('openTab', { detail: { tabType: 'eventstasks' } }));
      },
    }));
  }, [activeEvents, scope, siteNameMap]);

  // Transform documents into inbox items
  const docItems: InboxItemProps[] = useMemo(() => {
    return documents.map((doc) => ({
      type: 'document' as const,
      title: doc.title || doc.filename,
      subtitle: `${doc.doc_type || 'Document'} - ${formatFileSize(doc.file_size || 0)}`,
      timestamp: doc.created_at,
      outputs: [
        {
          type: 'event' as const,
          id: `doc-ev-${doc.id}`,
          title: `Document processed: ${doc.filename}`,
          severity: 'low' as const,
        },
      ],
      onOpenSource: () => {
        window.dispatchEvent(new CustomEvent('openTab', { detail: { tabType: 'dataroom' } }));
      },
    }));
  }, [documents]);

  // Combine all items
  const allItems: InboxItemProps[] = useMemo(() => {
    const combined = [...docItems, ...eventItems, ...MOCK_EMAILS, ...MOCK_AGENT];
    if (filter === 'documents') return combined.filter(i => i.type === 'document');
    if (filter === 'emails') return combined.filter(i => i.type === 'email');
    if (filter === 'agent') return combined.filter(i => i.type === 'agent');
    return combined;
  }, [docItems, eventItems, filter]);

  // Group by date
  const grouped = useMemo(() => {
    const groups: Record<string, InboxItemProps[]> = {};
    for (const item of allItems) {
      const group = getDateGroup(item.timestamp);
      if (!groups[group]) groups[group] = [];
      groups[group].push(item);
    }
    // Sort items within each group by timestamp desc
    for (const key of Object.keys(groups)) {
      groups[key].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    }
    return groups;
  }, [allItems]);

  const sortedGroupKeys = Object.keys(grouped).sort(
    (a, b) => (GROUP_ORDER[a] ?? 99) - (GROUP_ORDER[b] ?? 99)
  );

  // Stats
  const stats = useMemo(() => ({
    docs: docItems.length,
    emails: MOCK_EMAILS.length,
    calls: 0,
    events: allItems.reduce((sum, item) => sum + item.outputs.filter(o => o.type === 'event').length, 0),
    tasks: allItems.reduce((sum, item) => sum + item.outputs.filter(o => o.type === 'task').length, 0),
  }), [docItems, allItems]);

  return (
    <div className="eg-page-shell">
      {/* Header */}
      <div className="eg-page-header">
        <div className="eg-page-header-glow" />
        <div className="relative px-6 pt-5 pb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Inbox size={20} style={{ color: '#17c480' }} />
              <h1 className="eg-page-header-title">PROJECT INBOX</h1>
            </div>
            <button
              onClick={() => setUploadOpen(true)}
              className="eg-btn-primary flex items-center gap-2 text-sm px-4 py-2"
            >
              <Upload size={14} />
              Upload
            </button>
          </div>

          {/* Scope selector */}
          <div className="flex items-center gap-2 mb-3">
            <ScopeButton active={scope === 'portfolio'} onClick={() => setScope('portfolio')}>
              Portfolio
            </ScopeButton>
            <ScopeButton active={scope === 'site'} onClick={() => setScope('site')}>
              {siteName}
            </ScopeButton>
          </div>

          {/* Filter bar */}
          <div className="flex items-center gap-2">
            <Filter size={14} style={{ color: 'var(--color-text-muted)' }} />
            <FilterButton active={filter === 'all'} onClick={() => setFilter('all')}>All</FilterButton>
            <FilterButton active={filter === 'documents'} onClick={() => setFilter('documents')}>Documents</FilterButton>
            <FilterButton active={filter === 'emails'} onClick={() => setFilter('emails')}>Emails</FilterButton>
            <FilterButton active={filter === 'agent'} onClick={() => setFilter('agent')}>Agent</FilterButton>
          </div>
        </div>
        <div className="eg-page-accent-divider" />
      </div>

      {/* Content */}
      <div className="eg-page-content">
        <div className="px-6 py-5">
        {(docsLoading || portfolioEventsLoading) ? (
          <div className="eg-loading py-12">Loading inbox...</div>
        ) : allItems.length === 0 ? (
          <div className="eg-empty-state py-12">
            <div className="eg-empty-icon">
              <Inbox size={32} />
            </div>
            <p className="eg-empty-title">No inbox items</p>
            <p className="eg-empty-text">Upload documents or connect email to start receiving items.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {sortedGroupKeys.map((groupName) => (
              <div key={groupName}>
                <h3 className="text-xs font-semibold uppercase tracking-wider eg-text-muted mb-3">
                  {groupName}
                </h3>
                <div className="space-y-2">
                  {grouped[groupName].map((item, idx) => (
                    <InboxItem key={`${item.type}-${idx}`} {...item} />
                  ))}
                </div>
              </div>
            ))}

            {/* Summary */}
            <div className="mt-4">
              <InboxSummary stats={stats} />
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Upload modal */}
      <KnowledgeDropZone
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        sites={portfolioSites}
        defaultSiteId={siteId}
      />
    </div>
  );
};

// ── Sub-components ───────────────────────────────────────────────────

function ScopeButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className="px-3 py-1.5 text-sm font-medium rounded-md transition-colors"
      style={active
        ? { backgroundColor: 'rgba(23, 196, 128, 0.15)', color: '#17c480' }
        : { backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }
      }
    >
      {children}
    </button>
  );
}

function FilterButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors"
      style={active
        ? { backgroundColor: 'rgba(23, 196, 128, 0.15)', color: '#17c480' }
        : { backgroundColor: 'transparent', color: 'var(--color-text-muted)' }
      }
    >
      {children}
    </button>
  );
}

function formatFileSize(bytes: number): string {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let unitIndex = 0;
  let size = bytes;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(unitIndex > 0 ? 1 : 0)} ${units[unitIndex]}`;
}

export default ProjectInboxTab;
